import React, { useMemo, useState, useCallback } from 'react';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { Dashboard } from './pages/Dashboard';
import { Contracts } from './pages/Contracts';
import { Invoices } from './pages/Invoices';
import { DetailPanel } from './components/DetailPanel';

type ViewKey = 'dashboard' | 'contracts' | 'invoices';
type DetailTarget = { type: 'contract' | 'invoice'; id: string } | null;

const viewCopy: Record<ViewKey, { title: string; subtitle: string }> = {
  dashboard: {
    title: 'Cash position & forecast',
    subtitle: 'Key metrics that help the revenue team react fast.',
  },
  contracts: {
    title: 'Contracts & payment terms',
    subtitle: 'Milestones, target dates, and revenue schedules.',
  },
  invoices: {
    title: 'Invoice issuance board',
    subtitle: 'Stay on top of approvals, sending, and collections.',
  },
};

export const App: React.FC = () => {
  const [view, setView] = useState<ViewKey>('dashboard');
  const [detailTarget, setDetailTarget] = useState<DetailTarget>(null);

  const handleContractSelect = useCallback((id: string) => {
    setDetailTarget({ type: 'contract', id });
  }, []);

  const handleInvoiceSelect = useCallback((id: string) => {
    setDetailTarget({ type: 'invoice', id });
  }, []);

  const closePanel = useCallback(() => setDetailTarget(null), []);

  const content = useMemo(() => {
    switch (view) {
      case 'contracts':
        return <Contracts onSelectContract={handleContractSelect} />;
      case 'invoices':
        return <Invoices onSelectInvoice={handleInvoiceSelect} />;
      default:
        return (
          <Dashboard
            onSelectContract={handleContractSelect}
            onSelectInvoice={handleInvoiceSelect}
          />
        );
    }
  }, [view, handleContractSelect, handleInvoiceSelect]);

  return (
    <div className="app-shell">
      <Sidebar activeView={view} onSelect={setView} />
      <main className="main-panel">
        <Topbar title={viewCopy[view].title} subtitle={viewCopy[view].subtitle} />
        {content}
      </main>
      <DetailPanel target={detailTarget} onClose={closePanel} />
    </div>
  );
};
